// Shows a freshly-generated RECOVERY CODE once, and makes the user acknowledge they've saved it before
// continuing. Used after signup and when rotating the code in settings. The code is client-only — it was
// never sent to the server (only its derived key's verifier + the recovery-wrapped master key were), so this
// is the single moment it can be captured. Copy + download help the user stash it in a password manager.
import { useState } from 'react';
import * as s from './styles';

export interface RecoveryCodeCardProps {
  /** The recovery code to display (from signUp()'s result or setUpRecovery()). */
  code: string;
  /** The account email — used to name the downloaded file. */
  email?: string;
  /** Called once the user confirms they've saved it (checkbox + Continue). */
  onConfirm: () => void;
  /** Continue button label. Default "Continue". */
  confirmLabel?: string;
  /** Heading override. */
  title?: string;
}

export function RecoveryCodeCard({ code, email, onConfirm, confirmLabel = 'Continue', title }: RecoveryCodeCardProps) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked (permissions / insecure context) — the code is selectable, so this is non-fatal.
      setCopied(false);
    }
  };

  const download = () => {
    const body =
      `WiserWeb account recovery code\n${email ? `Account: ${email}\n` : ''}\n${code}\n\n` +
      `Keep this somewhere safe (e.g. a password manager). You'll need it — together with a one-time PIN ` +
      `emailed to you — to reset a forgotten password without losing your encrypted data. Anyone with this ` +
      `code and access to your email could reset your password, so treat it like a password.\n`;
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `wiserweb-recovery-code${email ? `-${email.replace(/[^a-z0-9.@_-]/gi, '_')}` : ''}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <h2 style={s.title}>{title ?? 'Save your recovery code'}</h2>
      <p style={s.sub}>
        This is the only way to reset a forgotten password without losing your encrypted data. Store it now —
        we can’t show it again.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <code style={s.codeBox} aria-label="Recovery code">{code}</code>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={{ ...s.secondaryBtn, flex: 1, marginTop: 0 }} onClick={() => void copy()}>
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
          <button type="button" style={{ ...s.secondaryBtn, flex: 1, marginTop: 0 }} onClick={download}>
            Download
          </button>
        </div>
        <label style={s.checkboxLbl}>
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
          I’ve saved my recovery code somewhere safe.
        </label>
        <button type="button" disabled={!saved} style={s.primaryBtn} onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </>
  );
}
